import type { MouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDebounce } from "@hooks/use-debounce";

import { Check, ChevronDownIcon, X } from "lucide-react";

import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  PopoverContent,
  PopoverPositioner,
  PopoverRoot,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";

import { TruncatedText } from "@components/truncated/truncated-text";
import { InputGroup } from "@components/ui/input-group";

import logger from "@utils/logger";

type BaseComboboxProps<T extends { id: string | number }> = {
  size?: "xs" | "sm" | "md";

  /**
   * List of static options (can be used alongside async search).
   * If provided, the combobox will display these options by default.
   * When `onSearch` is provided, these options serve as the initial/default options
   * before fetching new results.
   */
  options?: T[];

  /**
   * Async function to fetch options based on user input.
   * If provided, this function will be called with the user's search query.
   * When used in combination with `options`, `options` will be used initially
   * until a search query triggers a new fetch.
   */
  onSearch?: (query: string) => Promise<T[]>;

  /**
   * Function to retrieve the display label from an option.
   * Used for rendering selected values and search results.
   */
  getOptionLabel: (option: T) => string;

  /**
   * Custom function to render each option in the dropdown.
   * If not provided, `getOptionLabel` will be used.
   */
  renderOption?: (option: T) => ReactNode;

  /**
   * Debounce delay (in milliseconds) for the async search.
   * Helps control the frequency of API calls when users are typing.
   * Only applies when `onSearch` is provided.
   */
  searchDelay?: number;

  /**
   * Placeholder text for the input field when no option is selected.
   */
  placeholder?: string;

  /**
   * Callback function triggered when a user selects an option.
   */
  onSelect?: (option: T | null) => void;

  /**
   * The initially selected option (if any).
   */
  defaultValue?: T | null;

  /**
   * Function to customize what is displayed when no options are found.
   * By default, it shows "No results found...".
   *
   * @param query - The current search query entered by the user.
   * @param onSelect - A function to manually set a new option, useful for creating new entries.
   * @param onBlur - A function to manually blur the combobox.
   *
   * Example usage:
   * ```tsx
   * renderNoOptions={({ query, onSelect }) => (
   *   <Button onClick={() => onSelect({ id: Date.now(), name: query })}>
   *     Create "{query}"
   *   </Button>
   * )}
   * ```
   */
  renderNoOptions?: ({
    query,
    onSelect,
    onBlur,
  }: {
    query: string;
    onSelect: (option: T) => void;
    onBlur: () => void;
  }) => ReactNode;

  /**
   * Determines whether the combobox is disabled.
   * When `true`, the input field and dropdown interactions are disabled.
   */
  isDisabled?: boolean;

  /**
   * Enables a clearable option that allows users to remove their selection.
   * When `true`, a small "X" icon appears next to the selected value.
   * Clicking this icon clears the selection and resets the input field.
   *
   * If `onSelect` is provided, it will be called with `null` when the clear button is clicked.
   */
  clearable?: boolean;
};

type ComboboxProps<T extends { id: string | number }> = Omit<
  BaseComboboxProps<T>,
  "onSearch" | "searchDelay"
>;

type AsyncComboboxProps<T extends { id: string | number }> = BaseComboboxProps<T> & {
  onSearch: (query: string) => Promise<T[]>;
};

type SizeConfig = {
  left: string;
  right: string;
  iconSize: "xs" | "sm";
  iconPadding: string;
  iconGap: string;
  clearableIconSize: string;
  selectedIconSize: "xs" | "sm";
  fontSize: "xs" | "sm";
};

const SIZE_CONFIG: Record<"xs" | "sm" | "md", SizeConfig> = {
  xs: {
    left: "8px",
    right: "22px",
    iconSize: "xs",
    iconPadding: "8px",
    iconGap: "4px",
    clearableIconSize: "12px",
    selectedIconSize: "xs",
    fontSize: "xs",
  },
  sm: {
    left: "10px",
    right: "28px",
    iconSize: "sm",
    iconPadding: "10px",
    iconGap: "6px",
    clearableIconSize: "14px",
    selectedIconSize: "sm",
    fontSize: "sm",
  },
  md: {
    left: "12px",
    right: "36px",
    iconSize: "sm",
    iconPadding: "12px",
    iconGap: "6px",
    clearableIconSize: "14px",
    selectedIconSize: "sm",
    fontSize: "sm",
  },
};

function BaseCombobox<T extends { id: string | number }>({
  size = "sm",
  options: staticOptions,
  onSearch,
  getOptionLabel,
  renderOption,
  searchDelay = 300,
  placeholder = "Select an option...",
  onSelect,
  defaultValue,
  renderNoOptions,
  isDisabled = false,
  clearable = false,
}: BaseComboboxProps<T>) {
  const sizeConfig = SIZE_CONFIG[size];

  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const [isSearching, setIsSearching] = useState(false);

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, onSearch ? searchDelay : 0);

  const [selectedOption, setSelectedOption] = useState<T | null>(defaultValue ?? null);
  const [options, setOptions] = useState<T[]>(() => {
    if (defaultValue && staticOptions) {
      const existsInStaticOptions = staticOptions.some((option) => option.id === defaultValue.id);
      return existsInStaticOptions ? staticOptions : [defaultValue];
    }
    return defaultValue ? [defaultValue] : (staticOptions ?? []);
  });

  const isClearable = useMemo(
    () => clearable && selectedOption && !query.trim(),
    [clearable, selectedOption, query],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const latestRequestRef = useRef<number | null>(null);

  useEffect(() => {
    setSelectedOption(defaultValue ?? null);
  }, [defaultValue]);

  useEffect(() => {
    let isCancelled = false;

    async function fetchOptions() {
      if (!onSearch || !debouncedQuery.trim() || !isOpen) return;

      const requestId = Date.now();
      latestRequestRef.current = requestId;
      setIsSearching(true);

      try {
        const newOptions = await onSearch(debouncedQuery);
        if (latestRequestRef.current === requestId && !isCancelled) {
          setOptions((prevOptions) =>
            JSON.stringify(prevOptions) !== JSON.stringify(newOptions) ? newOptions : prevOptions,
          );
        }
      } catch (error) {
        logger.error("Search error:", error);
      } finally {
        if (latestRequestRef.current === requestId) {
          setIsSearching(false);
        }
      }
    }

    fetchOptions();

    return () => {
      isCancelled = true;
    };
  }, [debouncedQuery, onSearch, isOpen]);

  const filteredOptions = useMemo(() => {
    if (onSearch) return options;
    if (!query.trim()) return options;
    return options.filter((item) => getOptionLabel(item).toLowerCase().includes(query.toLowerCase()));
  }, [onSearch, options, query, getOptionLabel]);

  const handleInputClick = (e: MouseEvent) => {
    if (isDisabled) return;

    e.stopPropagation();
    inputRef.current?.focus();
    setQuery("");
    onOpen();
  };

  const handleSelect = useCallback(
    (option: T) => {
      if (selectedOption?.id === option.id) {
        onClose();
        return;
      }

      setSelectedOption(option);
      setQuery("");

      onSelect?.(option);
      if (onSearch) {
        setOptions([option]);
      }

      onClose();
    },
    [onSelect, onSearch, onClose, selectedOption],
  );

  const handleBlur = () => {
    setQuery("");
    if (onSearch) {
      setOptions(selectedOption ? [selectedOption] : (staticOptions ?? []));
    }
    setIsSearching(false);
    onClose();
  };

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      setSelectedOption(null);
      setQuery("");

      onSelect?.(null);
      if (onSearch) {
        setOptions(staticOptions ?? []);
      }

      onClose();
    },
    [onSelect, onSearch, onClose, staticOptions],
  );

  return (
    <PopoverRoot
      open={isOpen && !isDisabled}
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
      onExitComplete={handleBlur}
      positioning={{ placement: "bottom", sameWidth: true }}
      autoFocus={false}
      lazyMount
      unmountOnExit
    >
      <PopoverTrigger
        asChild
        position="relative"
        w="full"
        disabled={isDisabled}
        focusVisibleRing="none"
        tabIndex={-1}
      >
        <InputGroup
          w="full"
          bg="white"
          cursor={isDisabled ? "not-allowed" : "pointer"}
          endElement={
            isSearching && isOpen ? (
              <Icon
                size={sizeConfig.iconSize}
                color="gray.500"
              >
                <Spinner />
              </Icon>
            ) : (
              <HStack gap={sizeConfig.iconGap}>
                {isClearable && (
                  <IconButton
                    size="2xs"
                    color="gray.500"
                    _hover={{ color: "black" }}
                    h={size === "xs" ? "16px" : "20px"}
                    minW={size === "xs" ? "16px" : "20px"}
                    variant="outline"
                    onClick={handleClear}
                    disabled={isDisabled}
                    zIndex={100}
                  >
                    <Icon
                      w={sizeConfig.clearableIconSize}
                      h={sizeConfig.clearableIconSize}
                    >
                      <X />
                    </Icon>
                  </IconButton>
                )}
                <Icon
                  size={sizeConfig.iconSize}
                  color="gray.500"
                  transform={isOpen ? "rotate(180deg)" : undefined}
                  transition="transform 0.2s"
                >
                  <ChevronDownIcon />
                </Icon>
              </HStack>
            )
          }
          endElementProps={{
            onClick: handleInputClick,
            px: sizeConfig.iconPadding,
          }}
        >
          <>
            {/* Absolutely Positioned Placeholder */}
            {!query && (
              <TruncatedText
                position="absolute"
                left={sizeConfig.left}
                right={
                  isClearable
                    ? `calc(${sizeConfig.right} + ${sizeConfig.clearableIconSize} + ${
                        sizeConfig.iconGap
                      } + ${size === "xs" ? "4px" : "6px"})`
                    : sizeConfig.right
                }
                fontSize={sizeConfig.fontSize}
                textAlign="left"
                top="50%"
                transform="translateY(-50%)"
                color={selectedOption && !isDisabled ? "black" : "gray.500"}
                opacity={isOpen ? 0.5 : 1}
                zIndex={3}
                openDelay={300}
                content={selectedOption ? getOptionLabel(selectedOption) : placeholder}
                onClick={handleInputClick}
                disabled={isOpen || isDisabled}
                userSelect="none"
              >
                {selectedOption ? getOptionLabel(selectedOption) : placeholder}
              </TruncatedText>
            )}

            {/* Search Input */}
            <Input
              size={size}
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={handleInputClick}
              onFocus={onOpen}
              rounded="sm"
              cursor={isDisabled ? "not-allowed" : "pointer"}
              disabled={isDisabled}
              pr={sizeConfig.right}
              position="relative"
              zIndex={2}
            />
          </>
        </InputGroup>
      </PopoverTrigger>

      <Portal>
        <PopoverPositioner>
          <PopoverContent
            width="full"
            border="1px solid"
            borderColor="border.subtle"
            boxShadow="md"
            rounded="sm"
            maxH="60"
            overflow="auto"
            _focus={{ outline: "none" }}
            _active={{ outline: "none" }}
            animationName={{ _open: "slide-from-top,fade-in", _closed: "slide-to-top,fade-out" }}
            animationDuration="300ms"
          >
            <VStack
              gap={0}
              align="stretch"
            >
              {isSearching ? (
                <Flex
                  py={4}
                  justify="center"
                >
                  <Spinner />
                </Flex>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((item) => {
                  const isSelected = selectedOption && selectedOption.id === item.id;

                  return (
                    <Box
                      key={item.id}
                      py={2}
                      px={sizeConfig.left}
                      cursor="pointer"
                      bg={isSelected ? "gray.100" : "white"}
                      _hover={{ bg: "gray.100" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(item);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      fontSize={sizeConfig.fontSize}
                    >
                      <HStack justifyContent="space-between">
                        <TruncatedText
                          openDelay={300}
                          content={renderOption ? renderOption(item) : getOptionLabel(item)}
                        >
                          {renderOption ? renderOption(item) : getOptionLabel(item)}
                        </TruncatedText>

                        {isSelected && (
                          <Icon size={sizeConfig.iconSize}>
                            <Check />
                          </Icon>
                        )}
                      </HStack>
                    </Box>
                  );
                })
              ) : renderNoOptions && debouncedQuery.trim() ? (
                <Box
                  py={2}
                  px={sizeConfig.left}
                  color="gray.500"
                >
                  {renderNoOptions({ query: query, onSelect: handleSelect, onBlur: handleBlur })}
                </Box>
              ) : (
                <Text
                  py={2}
                  px={sizeConfig.left}
                  color="gray.500"
                >
                  No results
                </Text>
              )}
            </VStack>
          </PopoverContent>
        </PopoverPositioner>
      </Portal>
    </PopoverRoot>
  );
}

export function Combobox<T extends { id: string | number }>({ ...props }: ComboboxProps<T>) {
  return <BaseCombobox {...props} />;
}

export function AsyncCombobox<T extends { id: string | number }>({ ...props }: AsyncComboboxProps<T>) {
  return <BaseCombobox {...props} />;
}
